// src/services/api/cache.ts

/**
 * Cache implementation for API responses
 * This provides a simple in-memory cache with TTL (time-to-live) functionality
 */

// Define the cache item structure
export interface CacheItem<T> {
  data: T;
  timestamp: number;
}

// In-memory cache storage
const cache: Record<string, CacheItem<any>> = {};

// Default cache TTL (time-to-live) in milliseconds
export const CACHE_TTL = 30 * 1000; // 30 seconds

/**
 * Generates a cache key based on the endpoint and parameters
 * @param endpoint The API endpoint
 * @param params Object containing the parameters
 * @returns A string key for the cache
 */
export const generateCacheKey = (endpoint: string, params?: Record<string, any>): string => {
  if (!params) return endpoint;
  
  // Sort the params to ensure consistent keys regardless of object property order
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      if (params[key] !== undefined && params[key] !== null) {
        acc[key] = params[key];
      }
      return acc;
    }, {} as Record<string, any>);
  
  return `${endpoint}:${JSON.stringify(sortedParams)}`;
};

/**
 * Checks if a cache item is valid (not expired)
 * @param cacheItem The cache item to check
 * @returns Boolean indicating if the cache is still valid
 */
export const isCacheValid = <T>(cacheItem: CacheItem<T> | undefined): boolean => {
  if (!cacheItem) return false;
  
  const now = Date.now();
  return now - cacheItem.timestamp < CACHE_TTL;
};

/**
 * Invalidates cache entries that match a given prefix
 * @param prefix The prefix to match against cache keys
 */
export const invalidateCache = (prefix?: string): void => {
  if (!prefix) {
    // Clear entire cache if no prefix is provided
    Object.keys(cache).forEach(key => {
      delete cache[key];
    });
    return;
  }
  
  // Clear only cache entries that start with the prefix
  Object.keys(cache).forEach(key => {
    if (key.startsWith(prefix)) {
      delete cache[key];
    }
  });
};

/**
 * Stores an item in the cache
 * @param key The cache key
 * @param data The data to store
 * @param timestamp Optional timestamp (defaults to current time)
 */
export const setCacheItem = <T>(key: string, data: T, timestamp: number = Date.now()): void => {
  cache[key] = { data, timestamp };
};

/**
 * Retrieves an item from the cache if it exists and is valid
 * @param key The cache key
 * @returns The cached data or undefined if not found or expired
 */
export const getCacheItem = <T>(key: string): T | undefined => {
  const item = cache[key];
  if (item && isCacheValid(item)) {
    return item.data;
  }
  return undefined;
};

/**
 * Checks if a key exists in the cache and is valid
 * @param key The cache key to check
 * @returns Boolean indicating if the key exists and is valid
 */
export const hasCacheItem = (key: string): boolean => {
  return !!getCacheItem(key);
};

export default {
  generateCacheKey,
  isCacheValid,
  invalidateCache,
  setCacheItem,
  getCacheItem,
  hasCacheItem,
  CACHE_TTL
};
